import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { take, tap } from 'rxjs';
import { LaunchDarklyService } from '../../services/launchdarkly.service';
import { QuickFeedbackService } from './quick-feedback.service';

@Component({
    selector: 'app-quick-feedback',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './quick-feedback.component.html',
    styleUrls: ['../../../shared/composable-styles/input.scss', './quick-feedback.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeSlideIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(10px)' }),
                animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
        ]),
    ],
})
export class QuickFeedbackComponent implements OnDestroy {
    readonly #quickFeedbackService = inject(QuickFeedbackService);
    readonly #lds = inject(LaunchDarklyService);

    showQuickFeedback = this.#quickFeedbackService.showQuickFeedback;
    isQuickFeedbackEnabled = this.#lds.get('quick-feedback-auto-measure');
    feedbackText: string = '';
    currentStep = signal<STEPS>('rating');
    selectedRating: 0 | 1 | null = null;

    setFeedback(value: 0 | 1) {
        this.selectedRating = value;
        this.currentStep.set('feedback');
    }

    submitFeedback() {
        const context = this.#quickFeedbackService.quickFeedbackContext();
        if (!context) return;

        if (context.type === 'plan') {
            this.setTakeoffsFeedback();
        } else {
            this.setReviewFeedback();
        }
    }

    setTakeoffsFeedback() {
        if (this.selectedRating === null) return;
        this.#quickFeedbackService
            .setFeedbackScoreForTakeoffs(this.selectedRating, this.feedbackText)
            .pipe(
                take(1),
                tap(() => this.#resetComponent())
            )
            .subscribe();
    }

    setReviewFeedback() {
        if (this.selectedRating === null) return;
        this.#quickFeedbackService
            .setFeedbackScore(this.selectedRating, this.feedbackText)
            .pipe(
                take(1),
                tap(() => this.#resetComponent())
            )
            .subscribe();
    }

    #resetComponent() {
        this.currentStep.set('rating');
        this.selectedRating = null;
        this.feedbackText = '';
        this.#quickFeedbackService.reset();
    }

    ngOnDestroy() {
        this.#resetComponent();
    }
}

type STEPS = 'rating' | 'feedback';
